import { EventEmitter } from './event-emitter';
import {
  AccessTokenCookie,
  AccessTokenExpiresAtCookie,
} from '@ha/auth/common/cookie';

type TokenEvent = 'loaded' | 'error';

const skewMs = 60 * 1000;
const refreshLock = `__ha_refresh_lock__`;

// TODO: remove when typescript is upgraded
declare global {
  interface Lock {
    readonly mode: 'exclusive' | 'shared';
    readonly name: string;
  }

  interface LockInfo {
    clientId?: string;
    mode?: 'exclusive' | 'shared';
    name?: string;
  }

  interface LockManagerSnapshot {
    held?: LockInfo[] | undefined;
    pending?: LockInfo[] | undefined;
  }

  interface LockManagerRequestOptions {
    mode?: 'exclusive' | 'shared' | undefined;
    ifAvailable?: boolean | undefined;
    steal?: boolean | undefined;
    signal?: AbortSignal | undefined;
  }

  interface LockManager {
    request(
      name: string,
      callback: (lock: Lock) => Promise<any>,
    ): Promise<undefined>;
    request<T extends LockManagerRequestOptions>(
      name: string,
      options: T,
      callback: (
        lock: T['ifAvailable'] extends true ? Lock | null : Lock,
      ) => Promise<any>,
    ): Promise<undefined>;
    query(): Promise<LockManagerSnapshot>;
  }

  interface Navigator {
    locks: LockManager;
  }
}

export class Token extends EventEmitter<TokenEvent> {
  private token?: JWT;
  private timeoutId: NodeJS.Timeout | undefined;

  /**
   * @param cookie The cookie string to parse. Optional, if not provided, the document.cookie will be used.
   */
  private constructor() {
    super(['loaded', 'error']);
  }

  static getNew() {
    return new Token();
  }

  load() {
    const cookie = document.cookie;

    const token = AccessTokenCookie.parse(cookie) as JWT;
    const expires_at = AccessTokenExpiresAtCookie.parse(cookie);

    if (!token || !expires_at || !isPseudoJWT(token)) {
      // If there's no access_token, should we try calling the refresh endpoint?
      // It might be that a refresh token exists, but the access token has expired.
      this.emit('error', 'token not found or invalid');
      return;
    }

    this.token = token;

    const expiresTimestamp = Number(expires_at) * 1000;
    const expirationDate = new Date(expiresTimestamp);

    clearTimeout(this.timeoutId);

    const timeoutMs = Math.max(
      expiresTimestamp - new Date().getTime() - skewMs,
      skewMs,
    );
    this.timeoutId = setTimeout(() => this.refreshWithLock(), timeoutMs);

    this.emit('loaded', { token, expirationDate });
  }

  private async refreshWithLock(): Promise<void> {
    if (!navigator.locks) {
      return this.refresh();
    }

    return navigator.locks.request(refreshLock, async () => {
      await this.refresh();
    });
  }

  private async refresh(): Promise<void> {
    try {
      const expires_at = AccessTokenExpiresAtCookie.parse(document.cookie);
      if (expires_at) {
        const expiresTimestamp = Number(expires_at) * 1000;
        // we don't need to make a request if token was refreshed in another tab
        // and there's still a lot of time left until it expires
        if (expiresTimestamp - new Date().getTime() > skewMs) {
          this.load();
          return;
        }
      }

      const response = await fetch(`${window.location.origin}/oauth/refresh`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh token: ${response.statusText}`);
      }

      this.load();
    } catch (err) {
      this.emit('error', err);
    }
  }

  get authorizationHeaderValue() {
    if (this.token) {
      return `Bearer ${this.token}`;
    }

    return undefined;
  }
}

type JWT = `${string}.${string}.${string}`;

function isPseudoJWT(str: string) {
  const segments = str.split('.');

  return (
    segments.length === 3 &&
    segments.every(segment => segment.length > 0) &&
    segments[2]!.length > 10
  );
}
